import {
  faBuilding,
  faCoins,
  faTags,
  faTruck,
  faUsers,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { OrgScreenShell } from "../../../components/OrgShell/OrgScreenShell";
import { SectionNavItem } from "../../../components/OrgShell/SectionNavItem";
import { BadgeChip } from "../../../components/OrgShell/BadgeChip";
import { CustomerUpdateForm_VM } from "../vmHooks/useCustomerFormUpdateViewModel";
import { OrgSectionId } from "../models/organization-update.form";
import { DetailsSection } from "./sections/details-section.component";
import { ShippingSection } from "./sections/shipping-section.component";
import { PricingSection } from "./sections/pricing-section.component";
import { BrandsSection } from "./sections/brands-section.component";
import { UsersContactSection } from "./sections/users-contact-section.component";

interface SectionDefinition {
  id: OrgSectionId;
  label: string;
  icon: IconDefinition;
}

const CUSTOMER_SECTIONS: SectionDefinition[] = [
  { id: "details", label: "Details", icon: faBuilding },
  { id: "shipping", label: "Shipping", icon: faTruck },
  { id: "pricing", label: "Pricing", icon: faCoins },
  { id: "brands", label: "Brands", icon: faTags },
  { id: "users", label: "Users & contact", icon: faUsers },
];

const VENDOR_SECTIONS: SectionDefinition[] = [
  { id: "details", label: "Details", icon: faBuilding },
  { id: "shipping", label: "Shipping", icon: faTruck },
  { id: "users", label: "Users & contact", icon: faUsers },
];

export function CustomerUpdateForm(vm: CustomerUpdateForm_VM) {
  const {
    organization,
    isVendor,
    form,
    setForm,
    fieldErrors,
    showFieldErrors,
    validationSummary,
    isDirty,
    isLoading,
    activeSection,
    onSectionChange,
    onSectionPrefetch,
    onSave,
    onCancel,
    shippingDiffers,
    onShippingDiffersChange,
    pricing,
    brands,
    users,
  } = vm;

  const sections = isVendor ? VENDOR_SECTIONS : CUSTOMER_SECTIONS;
  const excludedCount = form.excludedManufacturerIds.length;

  return (
    <OrgScreenShell
      title={form.name}
      subtitle={
        isVendor
          ? "Vendor"
          : `Customer${
              organization?.navNumber ? ` · NAV ${organization.navNumber}` : ""
            }`
      }
      badges={
        <>
          {form.disabled ? (
            <BadgeChip tone="red">Deactivated</BadgeChip>
          ) : (
            <BadgeChip tone="green">Active</BadgeChip>
          )}
          {!isVendor && form.hidePrices && (
            <BadgeChip tone="orange">Prices hidden</BadgeChip>
          )}
          {!isVendor && excludedCount > 0 && (
            <BadgeChip tone="gray">
              {excludedCount} {excludedCount === 1 ? "brand" : "brands"}{" "}
              excluded
            </BadgeChip>
          )}
          {users.users !== undefined && (
            <BadgeChip tone="gray">
              {users.users.length} {users.users.length === 1 ? "user" : "users"}
            </BadgeChip>
          )}
        </>
      }
      nav={sections.map((section) => (
        <SectionNavItem
          key={section.id}
          label={section.label}
          icon={section.icon}
          active={activeSection === section.id}
          onClick={() => onSectionChange(section.id)}
          onPrefetch={
            onSectionPrefetch
              ? () => onSectionPrefetch(section.id)
              : undefined
          }
        />
      ))}
      footer={{
        onCancel,
        onSave,
        saveLabel: "Save changes",
        // Saving stays clickable — a click surfaces the validation errors.
        error: validationSummary,
        isDirty,
      }}
      isLoading={isLoading}
    >
      {activeSection === "details" && (
        <DetailsSection
          form={form}
          onChange={(patch) => setForm({ ...form, ...patch })}
          navNumber={organization?.navNumber}
          disabled={isVendor}
          isVendor={isVendor}
          required={!isVendor}
          errors={fieldErrors}
          showErrors={showFieldErrors}
        />
      )}
      {activeSection === "shipping" && (
        <ShippingSection
          form={form}
          onChange={(patch) => setForm({ ...form, ...patch })}
          differs={shippingDiffers}
          onDiffersChange={onShippingDiffersChange}
          required={!isVendor}
          errors={fieldErrors}
          showErrors={showFieldErrors}
        />
      )}
      {!isVendor && activeSection === "pricing" && (
        <PricingSection
          commission={pricing.commission}
          commissionOptions={pricing.commissionOptions}
          updateCommission={pricing.updateCommission}
          logoOptions={pricing.logoOptions}
          optionsReady={pricing.optionsReady}
          vatRate={form.vatRate}
          onVatChange={(value) => setForm({ ...form, vatRate: value })}
          hidePrices={form.hidePrices ?? false}
          onHidePricesChange={(next) => setForm({ ...form, hidePrices: next })}
        />
      )}
      {!isVendor && activeSection === "brands" && (
        <BrandsSection
          manufacturers={brands.manufacturers}
          isLoading={brands.isLoading}
          excludedIds={form.excludedManufacturerIds}
          onChange={(excludedManufacturerIds) =>
            setForm({ ...form, excludedManufacturerIds })
          }
        />
      )}
      {activeSection === "users" && (
        <UsersContactSection
          users={users.users}
          isLoading={users.isLoading}
          contact={{
            contactPerson: form.contactPerson,
            contactPhone: form.contactPhone,
            contactEmail: form.contactEmail,
          }}
          onContactChange={(contact) => setForm({ ...form, ...contact })}
          onCreateUser={users.onCreateUser}
          onDeleteUser={users.onDeleteUser}
          onUpdateUser={users.onUpdateUser}
          onToggleUserHidePrices={users.onToggleUserHidePrices}
          isVendor={isVendor}
        />
      )}
    </OrgScreenShell>
  );
}
